import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgIf } from '@angular/common';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';

@Component({
  selector: 'app-glucose-value',
  standalone: true,
  imports: [HighchartsChartModule, NgIf],
  templateUrl: './glucose-value.component.html',
  styleUrl: './glucose-value.component.css'
})
export class GlucoseValueComponent implements OnChanges {
  @Input() details: any;
  Highcharts: typeof Highcharts = Highcharts;
  averagesChartOptions: Highcharts.Options = {};
  gmiChartOptions: Highcharts.Options = {};
  showAveragesChart: boolean = true;
  showGmiChart: boolean = true;
  gmiNoDataFound: boolean = false;
  noDataFound: boolean = false;
  emptyMessage: string = '';
  private chartInstance: any;
  private gmiChartInstance: any;

  ngOnChanges(changes: SimpleChanges): void {
    // Reset flags
    this.noDataFound = false;
    this.gmiNoDataFound = false;
    this.emptyMessage = '';

    if (!this.details) {
      this.showAveragesChart = false;
      this.showGmiChart = false;
      this.gmiNoDataFound = false;
      this.noDataFound = true;
      this.emptyMessage = 'No Record found for selected date range';
      return;
    }
    // Force chart recreation like the main chart
    this.forceChartRecreation();
    this.buildAveragesChart();
    this.buildGmiChart();
    setTimeout(() => {
      this.resetChartZoom();
      this.resetGmiChartZoom();
    }, 500);
  }

  private buildAveragesChart(): void {
    const avgFastingSeries: any[] = [];
    const avgPostMealSeries: any[] = [];
    const avgGlucoseSeries: any[] = [];
    const mageSeries: any[] = [];
    const variabilitySeries: any[] = [];
    let dailyMaxValue = 0;

    const pushPoints = (arr: any[], src?: any[]) => {
      if (!src || !Array.isArray(src)) return;
      for (let i = 0; i < src.length; i++) {
        const d = src[i];
        // Parse timestamp and use Date.UTC to avoid timezone shift
        let dateStamp: number;
        if (typeof d.timestamp === 'string') {
          // Extract date parts from timestamp string to avoid timezone conversion
          const dateMatch = d.timestamp.match(/(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            // Use Date.UTC to create timestamp without timezone shift
            dateStamp = Date.UTC(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]));
          } else {
            dateStamp = Date.parse(d.timestamp);
          }
        } else {
          dateStamp = d.timestamp;
        }
        // Store original timestamp in custom property to display as-is
        arr.push({
          x: dateStamp,
          y: d.value,
          custom: {
            originalTimestamp: d.timestamp
          }
        });
        if (d.value > dailyMaxValue) dailyMaxValue = d.value;
      }
    };

    pushPoints(avgFastingSeries, this.details.avgFastingGlucoseByDate);
    pushPoints(avgPostMealSeries, this.details.avgPostMealGlucoseByDate);
    pushPoints(avgGlucoseSeries, this.details.avgGlucoseByDate);
    pushPoints(mageSeries, this.details.mageByDate);
    pushPoints(variabilitySeries, this.details.variabilityByDate);

    // Check if we have any actual data
    if (avgFastingSeries.length === 0 && avgPostMealSeries.length === 0 &&
      avgGlucoseSeries.length === 0 && mageSeries.length === 0 &&
      variabilitySeries.length === 0) {
      this.noDataFound = true;
      this.emptyMessage = 'No Record found for selected date range';
    }

    let gRangesDaily = [0, 70, 140, 180];
    if (dailyMaxValue >= 180 && dailyMaxValue <= 210) {
      gRangesDaily = [0, 70, 140, 180, dailyMaxValue + 15];
    } else if (dailyMaxValue >= 210 && dailyMaxValue <= 240) {
      gRangesDaily = [0, 70, 140, 180, 210, dailyMaxValue + 15];
    } else if (dailyMaxValue >= 240 && dailyMaxValue <= 270) {
      gRangesDaily = [0, 70, 140, 180, 210, 240, dailyMaxValue + 15];
    } else if (dailyMaxValue > 270) {
      gRangesDaily = [0, 70, 140, 180, 210, 240, 270, 350];
    }

    this.averagesChartOptions = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        panKey: 'shift',
        panning: { enabled: true, type: 'x' },
        zooming: { type: 'x' }
      },
      title: { text: 'Daily Averages' },
      xAxis: [{
        crosshair: true,
        lineWidth: 2,
        type: 'datetime',
        dateTimeLabelFormats: {
          // millisecond: '%H:%M:%S.%L',
          // second: '%H:%M:%S',
          // minute: '%H:%M',
          // hour: '%H:%M',
          millisecond: '%b %e',
          second: '%b %e',
          minute: '%b %e',
          hour: '%b %e',
          day: '%a,<br/>%b %e',
          week: '%a,<br/>%b %e',
          month: '%b \'%y',
          year: '%Y'
        }
      }],
      yAxis: {
        lineWidth: 2,
        title: { text: 'Values (mg/dl)' },
        tickPositioner: function () { return gRangesDaily; },
        plotBands: [
          { from: 0, to: 70, color: 'rgba(178, 34, 34, 0.4)' },
          { from: 70, to: 140, color: 'rgba(0, 128, 0, 0.4)' },
          { from: 140, to: 180, color: 'rgba(184, 134, 11, 0.4)' },
          { from: 180, to: 210, color: 'rgba(205, 92, 92, 0.4)' },
          { from: 210, to: 240, color: 'rgba(255, 69, 0, 0.4)' },
          { from: 240, to: 270, color: 'rgba(178, 34, 34, 0.4)' },
          { from: 270, to: 350, color: 'rgba(128, 0, 0, 0.4)' }
        ]
      },
      credits: { enabled: false },
      tooltip: {
        formatter: function () {
          if (this.x !== undefined) {
            // Use original timestamp as-is without timezone conversion
            const customData = (this.point as any).options?.custom || {};
            const originalTimestamp = customData.originalTimestamp;
            
            if (originalTimestamp) {
              // Display timestamp as-is without any conversion
              const timestampStr = String(originalTimestamp);
              // Extract date and time from timestamp string (format: YYYY-MM-DDTHH:mm:ss or similar)
              const dateMatch = timestampStr.match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2}(?:\.\d+)?)/);
              if (dateMatch) {
                const datePart = dateMatch[1];
                // const timePart = dateMatch[2].substring(0, 8); // Get HH:mm:ss only
                // Format date without timezone conversion
                const [year, month, day] = datePart.split('-');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const weekday = weekdays[dateObj.getDay()];
                const monthName = monthNames[parseInt(month) - 1];
                const label = weekday + ', ' + monthName + ' ' + day;
                // return label + ' at ' + timePart + '</b> <br> <b>' + this.y + ' mg/dl</b>';
                return label + '</b> <br> <b>' + this.y + ' mg/dl</b>';
              }
              // Fallback: use timestamp as-is
              return timestampStr + '</b> <br> <b>' + this.y + ' mg/dl</b>';
            }
            // Fallback if no original timestamp
            return this.y + ' mg/dl';
          }
          return this.y + ' mg/dl';
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: { color: '#fff', fontSize: '10pt', fontFamily: 'Urbanist, sans-serif' }
      },
      plotOptions: {
        line: { dataLabels: { enabled: false }, enableMouseTracking: true },
        // Make markers inherit series color and use a smaller radius for the Daily Averages chart
        series: { marker: { symbol: 'circle', enabled: true, radius: 3 } }
      },
      series: [
        { name: 'Avg Fasting', data: avgFastingSeries, type: 'line', color: '#E58009' },
        { name: 'Avg Post Meal', data: avgPostMealSeries, type: 'line', color: '#089bab' },
        { name: 'Avg Glucose', data: avgGlucoseSeries, type: 'line', color: '#6f42c1' },
        { name: 'MAGE', data: mageSeries, type: 'line', color: '#d63384' },
        { name: 'Glucose Variability', data: variabilitySeries, type: 'line', color: '#198754' }
      ]
    };

    // Add load event to capture chart instance for zoom reset
    if (!this.averagesChartOptions.chart) this.averagesChartOptions.chart = {};
    if (!this.averagesChartOptions.chart.events) (this.averagesChartOptions.chart as any).events = {};
    (this.averagesChartOptions.chart as any).events.load = this.chartCallback;
  }

  private buildGmiChart(): void {
    const gmiSeries: any[] = [];
    let maxGmi = 0;

    const pushPoints = (arr: any[], src?: any[]) => {
      if (!src || !Array.isArray(src)) return;
      for (let i = 0; i < src.length; i++) {
        const d = src[i];
        // Parse timestamp and use Date.UTC to avoid timezone shift
        let dateStamp: number;
        if (typeof d.timestamp === 'string') {
          // Extract date parts from timestamp string to avoid timezone conversion
          const dateMatch = d.timestamp.match(/(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            // Use Date.UTC to create timestamp without timezone shift
            dateStamp = Date.UTC(parseInt(dateMatch[1]), parseInt(dateMatch[2]) - 1, parseInt(dateMatch[3]));
          } else {
            dateStamp = Date.parse(d.timestamp);
          }
        } else {
          dateStamp = d.timestamp;
        }
        // Store original timestamp in custom property to display as-is
        arr.push({
          x: dateStamp,
          y: d.value,
          custom: {
            originalTimestamp: d.timestamp
          }
        });
        if (d.value > maxGmi) maxGmi = d.value;
      }
    };

    pushPoints(gmiSeries, this.details.gmiByDate);

    let gmiRangesDaily = [3.31, 5.0, 6.66, 7.62];
    if (!gmiSeries.length) {
      this.gmiNoDataFound = true;
      gmiRangesDaily = [3.31, 5.0, 6.66, 7.62, 8.32, 8.98, 9.64, 11.69];
    } else {
      this.gmiNoDataFound = false;
      maxGmi = Number(maxGmi.toFixed(1));
      if (maxGmi >= 7.62 && maxGmi <= 8.32) {
        gmiRangesDaily = [3.31, 5.0, 6.66, 7.62, Number((maxGmi + 0.5).toFixed(1))];
      } else if (maxGmi >= 8.32 && maxGmi <= 8.98) {
        gmiRangesDaily = [3.31, 5.0, 6.66, 7.62, 8.32, Number((maxGmi + 0.5).toFixed(1))];
      } else if (maxGmi >= 8.98 && maxGmi <= 9.64) {
        gmiRangesDaily = [3.31, 5.0, 6.66, 7.62, 8.32, 8.98, Number((maxGmi + 0.5).toFixed(1))];
      } else if (maxGmi > 9.64) {
        gmiRangesDaily = [3.31, 5.0, 6.66, 7.62, 8.32, 8.98, 9.64, 11.69];
      }
    }

    const gmiMax = gmiRangesDaily[gmiRangesDaily.length - 1];
    const gmiPlotBands = this.gmiNoDataFound ? [] : [
      { from: 3.31, to: 5.0, color: 'rgba(178, 34, 34, 0.4)' },
      { from: 5.0, to: 6.66, color: 'rgba(0, 128, 0, 0.4)' },
      { from: 6.66, to: 7.62, color: 'rgba(184, 134, 11, 0.4)' },
      { from: 7.62, to: 8.32, color: 'rgba(205, 92, 92, 0.4)' },
      { from: 8.32, to: 8.98, color: 'rgba(255, 69, 0, 0.4)' },
      { from: 8.98, to: 9.64, color: 'rgba(178, 34, 34, 0.4)' },
      { from: 9.64, to: 11.69, color: 'rgba(128, 0, 0, 0.4)' }
    ];
    const gmiTickPositions = this.gmiNoDataFound ? undefined : gmiRangesDaily;
    const gmiYAxisLabels = { enabled: !this.gmiNoDataFound };
    const gmiGridLineWidth = this.gmiNoDataFound ? 0 : undefined;

    this.gmiChartOptions = {
      chart: {
        backgroundColor: '#F9F9F9',
        type: 'line',
        panKey: 'shift',
        panning: { enabled: true, type: 'x' },
        zooming: { type: 'x' }
      },
      title: { text: 'Daily GMI' },
      xAxis: [{
        crosshair: true,
        lineWidth: 2,
        type: 'datetime',
        dateTimeLabelFormats: {
          // millisecond: '%H:%M:%S.%L',
          // second: '%H:%M:%S',
          // minute: '%H:%M',
          // hour: '%H:%M',
          millisecond: '%b %e',
          second: '%b %e',
          minute: '%b %e',
          hour: '%b %e',
          day: '%a,<br/>%b %e',
          week: '%a,<br/>%b %e',
          month: '%b \'%y',
          year: '%Y'
        }
      }],
      yAxis: {
        lineWidth: 2,
        title: { text: 'GMI %' },
        min: 3.31,
        max: gmiMax,
        tickPositions: gmiTickPositions,
        plotBands: gmiPlotBands,
        labels: gmiYAxisLabels,
        gridLineWidth: gmiGridLineWidth
      },
      credits: { enabled: false },
      tooltip: {
        formatter: function () {
          if (this.x !== undefined) {
            // Use original timestamp as-is without timezone conversion
            const customData = (this.point as any).options?.custom || {};
            const originalTimestamp = customData.originalTimestamp;
            
            if (originalTimestamp) {
              // Display timestamp as-is without any conversion
              const timestampStr = String(originalTimestamp);
              // Extract date and time from timestamp string (format: YYYY-MM-DDTHH:mm:ss or similar)
              const dateMatch = timestampStr.match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2}(?:\.\d+)?)/);
              if (dateMatch) {
                const datePart = dateMatch[1];
                // const timePart = dateMatch[2].substring(0, 8); // Get HH:mm:ss only
                // Format date without timezone conversion
                const [year, month, day] = datePart.split('-');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                const weekday = weekdays[dateObj.getDay()];
                const monthName = monthNames[parseInt(month) - 1];
                const label = weekday + ', ' + monthName + ' ' + day;
                // return label + ' at ' + timePart + '</b> <br> <b>' + this.y + '%</b>';
                return label + '</b> <br> <b>' + this.y + '%</b>';
              }
              // Fallback: use timestamp as-is
              return timestampStr + '</b> <br> <b>' + this.y + '%</b>';
            }
            // Fallback if no original timestamp
            return this.y + '%';
          }
          return this.y + '%';
        },
        backgroundColor: 'rgba(0,0,0, 0.6)',
        borderWidth: 1,
        shadow: true,
        style: { color: '#fff', fontSize: '10pt', fontFamily: 'Urbanist, sans-serif' }
      },
      plotOptions: {
        line: { dataLabels: { enabled: false }, enableMouseTracking: true },
        series: { marker: { symbol: 'circle', enabled: true, radius: 2 } }
      },
      series: [
        {
          name: 'GMI',
          data: gmiSeries,
          type: 'line',
          color: '#007bff',
          marker: { lineColor: '#007bff', fillColor: '#007bff' }
        }
      ]
    };

    if (!this.gmiChartOptions.chart) this.gmiChartOptions.chart = {};
    if (!this.gmiChartOptions.chart.events) (this.gmiChartOptions.chart as any).events = {};
    (this.gmiChartOptions.chart as any).events.load = this.gmiChartCallback;
  }

  private chartCallback = (chartEvent: any) => {
    const actualChart = chartEvent.target;
    this.chartInstance = actualChart;
  }

  private gmiChartCallback = (chartEvent: any) => {
    const actualChart = chartEvent.target;
    this.gmiChartInstance = actualChart;
  }

  private resetChartZoom(): void {
    const chart = this.chartInstance;
    if (chart && chart.xAxis && chart.xAxis[0] && typeof chart.xAxis[0].setExtremes === 'function') {
      chart.xAxis[0].setExtremes(null, null);
    }
  }

  private resetGmiChartZoom(): void {
    const chart = this.gmiChartInstance;
    if (chart && chart.xAxis && chart.xAxis[0] && typeof chart.xAxis[0].setExtremes === 'function') {
      chart.xAxis[0].setExtremes(null, null);
    }
  }

  private forceChartRecreation(): void {
    this.showAveragesChart = false;
    this.showGmiChart = false;
    setTimeout(() => {
      this.showAveragesChart = true;
      this.showGmiChart = true;
    }, 10);
  }
}
